/**
 * 設定 → 見積・請求（#131 の2回目、docs/47 §12.7。#142 段3 で「見積」から＝分割の既定を足した、docs/52 §6。#153 で自社情報から9つを移した、docs/62 §4）。
 * #154（docs/63、Figma「見積・請求（10/9）」⑦〜⑩）で iOS のまとまり（docs/10 §9.15）に。現場力・営業力は同じコード（違いは言葉と使う機能だけ）。DB・API は変えていない。
 *   ・上の帯「‹ 設定｜自動で保存されます」（PC の2列は「‹ 設定」を出さず、題の右に）、大きな題「見積・請求」
 *   ・節は仕事の順：見積を作るとき（新しい行の仕入れ・既定の並べ方・分割の既定 ›）／内訳のない一式の知らせ（社内用）／{vendorQuote}からの取込（vendorQuote の会社だけ）／
 *     見積書の PDF と印刷（{takeoffSheet}は takeoff の会社だけ）／出した見積・請求書（id unissue）／入金（API が値を返す会社＝sales だけ）
 *   ・スイッチ・仕入れ・並べ方は押した（選んだ）瞬間に保存（今どおり）。数の欄（一式の知らせ 割合・金額／粗利率の既定／値引きにする上限）は
 *     ~~右上の「保存」1つ（★2 a）~~ → **自動で保存**（S-00008 の続き、10/10 射場さん：設定の画面は自動で保存に揃える。lib/autoSave.tsx）＝欄を離れたとき・Enter・画面を離れるとき。
 *     送るのは直した欄と保存してある会社名だけ（PUT /company は省いた欄を変えない）。範囲の外は行の下に朱の1行で、送らない（そのまま離れようとしたら確かめる）
 *   ・名前は短く、説明は節の見出しの「？」（今の文。節に「？」が2つ以上あったものは1つにつなぐ）
 *   ・新しい行の仕入れ：自社（既定）／未定。自社＝自社で施工する行（仕入れ 0、「仕入れ未入力」に数えない）。未定＝仕入れが入るまで数える（ルール7）。今ある行は変わらない
 *   ・分割の既定は下の画面（★1 a。SettingsInstallmentsPage、/settings/estimate/installments）。ここには「分割の既定 ›」の1行（右に今の回の名前）
 *   ・担当者は見るだけ（全部の行を押せない形・題の下に注1行・保存の様子なし）。会社名がまだ無い会社は保存できない（先に自社情報で会社名）
 *   ・/settings/estimate#unissue で来たら「出した見積・請求書」の節まで送る（前の「見積・請求書の取消しと改ページ」の行・古いリンク）
 *   ・#213（docs/40 §2）：節「{referrer}手数料」＝スイッチ（モジュール referral の ON/OFF。押した瞬間に保存・/me を取り直す）と
 *     上乗せ後の単価の丸め（1円／10円／100円。切り上げ。{vendorQuote}の取込も同じ）。{vendorQuote}の節が無い会社は、ここに粗利率の既定も
 */
import {
  AXES_MSG,
  BUNDLE_MODE_LABEL,
  CLOSING_MSG,
  RECURRING_MSG,
  COST_DEFAULT_LABEL,
  MARKUP_ROUNDS,
  REFERRAL_MSG,
  type MarkupRound,
  CompanyUpdate,
  CostDefault,
  ESTIMATE_PC_MSG,
  InstallmentDefaults,
  SETTINGS_MSG,
  layoutAxesFor,
  type Company,
  type EstimateSettings,
  type EstimateSettingsUpdate,
  type InstallmentDefault,
  type LayoutAxis, MAIL_KIND_LABEL, MAIL_MSG, type MailTemplates, DELIVERY_NOTE_MSG, REVENUE_BASIS_LABEL, type RevenueBasis } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { api, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { BackButton, useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupList, GroupNavRow, GroupNumberRow, GroupRow, GroupSection, GroupSelectRow, GroupSwitchRow, GroupValueRow, InstallmentRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { useUses, uses } from "../core/uses.js";
import { t, useLabels } from "../labels.js";
import { AutoSaveNote, AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";
import { useSettingsPane } from "../lib/settingsPane.js";

/** 押した瞬間に保存するスイッチ（#153 で自社情報から移した5つ） */
type Flags = Pick<Company, "allowQuoteUnissue" | "pdfPageBreakPerSheet" | "printDitto" | "printScopeNotes" | "printTakeoffSheet" | "referralFee">;
/** 自動で保存する数の欄（★2 a → S-00008 の続き） */
const NUM_KEYS = ["lumpsumWarnPct", "lumpsumWarnYen", "vendorMarginPct", "shortfallDiscountLimit"] as const;
type NumKey = (typeof NUM_KEYS)[number];
type Nums = Pick<Company, NumKey>;
const numsOf = (c: Company): Nums => ({ lumpsumWarnPct: c.lumpsumWarnPct, lumpsumWarnYen: c.lumpsumWarnYen, vendorMarginPct: c.vendorMarginPct, shortfallDiscountLimit: c.shortfallDiscountLimit });
/** 数の欄の形の決まり（PUT /company と同じ。範囲の外は送らずに行の下に朱の1行） */
const NumUpdate = CompanyUpdate.pick({ lumpsumWarnPct: true, lumpsumWarnYen: true, vendorMarginPct: true, shortfallDiscountLimit: true });

export const ESTIMATE_SETTINGS_KEY = ["company-estimate-settings"] as const;
const KEY = ESTIMATE_SETTINGS_KEY;
const INSTALLMENTS_PATH = "/settings/estimate/installments";

/** 回の名前を「・」でつなぐ（分割の既定 › の右・既定に戻すの括弧） */
const namesOf = (xs: InstallmentDefault[]) => xs.map((x) => x.label).join("・");

/** 節に「？」が2つ以上あったものは1つにつなぐ（docs/63 §2）：行の名前ごとに1段 */
function helpOf(parts: Array<[string, string]>): ReactNode {
  return parts.map(([k, v]) => (
    <span key={k} className="helpp">
      <b>{k}</b>
      {`　${v}`}
    </span>
  ));
}

export function SettingsEstimatePage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const me = useMe();
  const can = useUses();
  const sales = !!me.data?.tenant.modules.includes("sales");
  const L = useLabels();
  const loc = useLocation();
  const pane = useSettingsPane();
  const costId = useId();
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: KEY, queryFn: () => api<EstimateSettings>("GET", "/company/estimate-settings") });
  // メールの文面の行の右の値（#225）
  const mailTpl = useQuery({ queryKey: ["mail-templates"], queryFn: () => api<MailTemplates>("GET", "/company/mail-templates") });
  const cq = useQuery({ queryKey: ["company"], queryFn: () => api<Company>("GET", "/company") });
  /** スイッチ・並べ方の入れかけ（数の欄は下の useAutoSave） */
  const [cf, setCf] = useState<Company | null>(null);
  useEffect(() => {
    if (cq.data && !cf) setCf(cq.data);
  }, [cq.data, cf]);
  // 前の設定のメニューの「見積・請求書の取消しと改ページ」・古いリンク（/settings/company#unissue → ここ）から来たら、その節まで送る
  useEffect(() => {
    if (cf && q.data && loc.hash === "#unissue") document.getElementById("unissue")?.scrollIntoView({ block: "start" });
  }, [cf, q.data, loc.hash]);
  const FLAG_NAME: Record<keyof Flags, string> = {
    allowQuoteUnissue: "出した見積・請求書の取消し",
    pdfPageBreakPerSheet: "内訳ごとの改ページ",
    printDitto: "印刷の「〃」", // #129
    printScopeNotes: "別途の一覧の印字", // #129 → #308
    printTakeoffSheet: `${L.takeoffSheet}の添付`, // #130（#142 段6 No.29：言葉）
    referralFee: t(REFERRAL_MSG.feature), // #213
  };
  /** PUT /company は会社名が要る＝保存してある会社名と、直した欄だけを送る（省いた欄は変わらない） */
  const putCompany = (patch: Partial<CompanyUpdate>) => api<Company>("PUT", "/company", { name: cq.data!.name, ...patch });
  const saveFlag = useMutation({
    mutationFn: (patch: Partial<Flags>) => putCompany(patch),
    onSuccess: (c, patch) => {
      qc.setQueryData(["company"], c);
      void qc.invalidateQueries({ queryKey: ["estimate"] }); // 出した見積の「取り消して下書きに戻す」の出る／出ない
      void qc.invalidateQueries({ queryKey: ["invoice"] });
      // #213：パートナー手数料はモジュール（/me の modules）。取り直して、顧客・見積の画面に欄を出す／隠す
      if ("referralFee" in patch) void qc.invalidateQueries({ queryKey: ME_KEY });
      const [k, v] = Object.entries(patch)[0] as [keyof Flags, boolean];
      toast(`${FLAG_NAME[k]}を${v ? "オン" : "オフ"}にしました。`);
    },
    onError: (e, patch) => {
      const [k, v] = Object.entries(patch)[0] as [keyof Flags, boolean];
      setCf((f) => (f ? { ...f, [k]: !v } : f));
      toast(messageOf(e), undefined, "error");
    },
  });
  /** 既定の並べ方（#129）：選んだ瞬間に保存する（スイッチと同じ） */
  const saveAxis = useMutation({
    mutationFn: (axis: LayoutAxis) => putCompany({ defaultLayoutAxis: axis }),
    onSuccess: (c) => {
      qc.setQueryData(["company"], c);
      toast(`既定の表示順を「${t(AXES_MSG.axis[c.defaultLayoutAxis])}」にしました。`);
    },
    onError: (e) => {
      setCf((f) => (f && cq.data ? { ...f, defaultLayoutAxis: cq.data.defaultLayoutAxis } : f));
      toast(messageOf(e), undefined, "error");
    },
  });
  /** 売上の基準（#266、docs/96）：今月の実績の売上をどの日で数えるか。選んだ瞬間に保存する（検収日は検収書＝1b から） */
  const saveBasis = useMutation({
    mutationFn: (b: RevenueBasis) => putCompany({ revenueBasis: b }),
    onSuccess: (c) => {
      qc.setQueryData(["company"], c);
      void qc.invalidateQueries({ queryKey: ["home"] });
      toast(`${DELIVERY_NOTE_MSG.revenueBasis}を「${REVENUE_BASIS_LABEL[c.revenueBasis ?? "invoice"]}」にしました。`);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  /** 上乗せ後の単価の丸め（#213）：選んだ瞬間に保存する */
  const saveRound = useMutation({
    mutationFn: (r: MarkupRound) => putCompany({ markupRound: r }),
    onSuccess: (c) => {
      qc.setQueryData(["company"], c);
      qc.removeQueries({ queryKey: ["estimate"] }); // 開いている見積の行の詳細・取込の1枚の単位
      toast(`${REFERRAL_MSG.round}を「${REFERRAL_MSG.roundUnit.replace("{n}", String(c.markupRound))}」にしました。`);
    },
    onError: (e) => {
      setCf((f) => (f && cq.data ? { ...f, markupRound: cq.data.markupRound } : f));
      toast(messageOf(e), undefined, "error");
    },
  });
  const roundId = useId();
  /** 新しい行の仕入れ（#131 の2回目）：押した瞬間に保存 */
  const save = useMutation({
    mutationFn: (v: EstimateSettingsUpdate) => api<EstimateSettings>("PUT", "/company/estimate-settings", v),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      // 開いている見積の画面の「新しい行の既定」を取り直す
      qc.removeQueries({ queryKey: ["estimate"] });
      toast(ESTIMATE_PC_MSG.costDefaultSaved.replace("{v}", COST_DEFAULT_LABEL[v.defaultCostSource]));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  /** 締め請求書の明細の形（#267 ★3）：押した瞬間に保存 */
  const saveClosing = useMutation({
    mutationFn: (v: EstimateSettingsUpdate) => api<EstimateSettings>("PUT", "/company/estimate-settings", v),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      void qc.invalidateQueries({ queryKey: ["closing"] });
      toast(`${CLOSING_MSG.closingModeLabel}を「${BUNDLE_MODE_LABEL[v.closingMode]}」にしました`);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  /** 定期請求（#268）：下書きの自動作成・作成時期。押した瞬間に保存。スイッチは部品の値で描く（答えを待たずに切り替わる） */
  const [recAuto, setRecAuto] = useState<boolean | null>(null);
  useEffect(() => {
    if (q.data) setRecAuto(q.data.recurringAuto);
  }, [q.data?.recurringAuto]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveRec = useMutation({
    mutationFn: (v: EstimateSettingsUpdate) => api<EstimateSettings>("PUT", "/company/estimate-settings", v),
    onSuccess: (v, sent) => {
      qc.setQueryData(KEY, v);
      void qc.invalidateQueries({ queryKey: ["recurring"] });
      void qc.invalidateQueries({ queryKey: ["group-recurring"] });
      toast(sent.recurringLeadDays !== undefined ? RECURRING_MSG.leadSaved(v.recurringLeadDays) : v.recurringAuto ? RECURRING_MSG.autoOn : RECURRING_MSG.autoOff);
    },
    onError: (e) => {
      setRecAuto(q.data?.recurringAuto ?? null);
      toast(messageOf(e), undefined, "error");
    },
  });
  /** 会社名がまだ無い会社は保存できない（PUT /company は会社名が要る） */
  const hasName = !!cq.data?.name;
  const toggleFlag = (patch: Partial<Flags>) => {
    if (!hasName) {
      toast(SETTINGS_MSG.needCompanyName);
      return;
    }
    setCf((f) => (f ? { ...f, ...patch } : f));
    saveFlag.mutate(patch);
  };
  /** 数の欄は自動で保存（S-00008 の続き）：直した欄だけ。範囲の外・会社名が無いときは送らない */
  const a = useAutoSave<Nums>({
    server: cq.data ? numsOf(cq.data) : null,
    enabled: admin,
    send: async (v, base) => {
      const c = await putCompany(Object.fromEntries(NUM_KEYS.filter((k) => v[k] !== base[k]).map((k) => [k, v[k]])) as Partial<CompanyUpdate>);
      qc.setQueryData(["company"], c);
      return numsOf(c);
    },
    check: (v) => {
      if (!hasName) return { status: SETTINGS_MSG.autoSaveNoName, note: SETTINGS_MSG.autoSaveNoNameNote };
      const r = NumUpdate.safeParse(v);
      if (r.success) return null;
      const errors: Record<string, string> = {};
      for (const i of r.error.issues) {
        const k = String(i.path[0] ?? "");
        if (k && !errors[k]) errors[k] = i.message;
      }
      return { status: SETTINGS_MSG.autoSaveFailed, errors };
    },
    hide: (v, base) => (cq.data ? { url: "/api/v1/company", method: "PUT", body: { name: cq.data.name, ...Object.fromEntries(NUM_KEYS.filter((k) => v[k] !== base[k]).map((k) => [k, v[k]])) } } : null),
  });
  const nums = a.form;
  const cErrors = a.errors;
  const setNum = (patch: Partial<Nums>) => a.set(patch);

  const back = useBackNav({ to: "/settings", label: "設定" }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(ESTIMATE_PC_MSG.settingsTitle);

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (cq.isError) return <ErrorText>{messageOf(cq.error)}</ErrorText>;
  if (!q.data || !cf || !nums || !me.data) return <p className="hint">読み込んでいます…</p>;
  const now = save.isPending && save.variables?.defaultCostSource ? save.variables.defaultCostSource : q.data.defaultCostSource;
  const instNames = namesOf(q.data.installments ?? q.data.installmentsProduct);

  // 前の「保存」の場所に、保存の様子（S-00008 の続き）
  const saveBtn = admin ? <AutoSaveStatus state={a.state} testId="estimate-autosave" /> : null;

  return (
    <div className={"ppage estpage" + (pane.twoCol ? " is-pane" : "")} data-testid="estimate-settings" {...a.formProps}>
      {pane.twoCol ? null : (
        <div className="cotop">
          <BackButton back={back} />
          <span className="cotop-sp" />
          {saveBtn}
        </div>
      )}
      {back.dialog}
      {guard.dialog}
      <div className="ptitle-row">
        <h1 className="ptitle">{ESTIMATE_PC_MSG.settingsTitle}</h1>
        {pane.twoCol ? saveBtn : null}
      </div>
      <AutoSaveNote state={a.state} testId="estimate-autosave-note" />
      {!admin ? (
        <p className="est-note" id="estimateMemberNote" data-testid="estimate-member-note">
          {SETTINGS_MSG.memberNote}
        </p>
      ) : null}

      <GroupSection
        title={SETTINGS_MSG.sectionCreate}
        help={
          <Help
            testId="help-est-create"
            text={helpOf([
              [ESTIMATE_PC_MSG.costDefaultLabel, t(`自社：仕入れ 0 として扱い、「仕入れ未入力」に数えません。{vendor}を付けた行${can("vendorQuote") ? "・{vendorQuote}から取り込んだ行" : ""}は、自動でそちらに変わります。未定：仕入れを入れるまで「仕入れ未入力」に数えます。今ある行は変わりません。`)],
              [SETTINGS_MSG.axis, "新しく作る見積の表示順です。作成中・発行済みの見積は変わりません。見積ごとに頭の帯の「表示順」で変えられます。"],
              [ESTIMATE_PC_MSG.installmentsTitle, ESTIMATE_PC_MSG.installmentsHelp],
            ])}
          />
        }
        data-testid="est-sec-create"
      >
        <GroupList>
          <GroupValueRow
            title={ESTIMATE_PC_MSG.costDefaultLabel}
            titleId={costId}
            className="grow-seg"
            value={
              <Segmented
                options={CostDefault.options.map((o) => ({ value: o, label: COST_DEFAULT_LABEL[o], testId: `cost-default-${o}` }))}
                value={now}
                onChange={(o) => now !== o && save.mutate({ defaultCostSource: o })}
                labelledBy={costId}
                disabled={!admin || save.isPending}
                testId="cost-default"
              />
            }
          />
          <GroupSelectRow
            id="st_axis"
            label={SETTINGS_MSG.axis}
            className="is-end"
            value={cf.defaultLayoutAxis}
            options={layoutAxesFor({ trades: can("trades"), places: can("places") }, cf.defaultLayoutAxis).map((a) => ({ value: a, label: t(AXES_MSG.axis[a]) }))}
            disabled={!admin}
            onChange={(v) => {
              const a = v as LayoutAxis;
              if (!hasName) {
                toast(SETTINGS_MSG.needCompanyName);
                return;
              }
              setCf((f) => (f ? { ...f, defaultLayoutAxis: a } : f));
              saveAxis.mutate(a);
            }}
            selectTestId="st-axis"
          />
          <GroupNavRow title={ESTIMATE_PC_MSG.installmentsTitle} value={instNames} valueTestId="installments-value" to={INSTALLMENTS_PATH} buttonId="toInstallments" testId="installments-row" />
          {/* 締め請求書の明細の形（#267 ★3。sales の会社だけ）：既定は 1枚1行。375px でも名前と重ならない選ぶ欄（既定の表示順と同じ形） */}
          {me.data.tenant.modules.includes("sales") ? (
            <GroupSelectRow
              id="st_closing_mode"
              label={CLOSING_MSG.closingModeLabel}
              className="is-end"
              value={saveClosing.isPending && saveClosing.variables?.closingMode ? saveClosing.variables.closingMode : q.data.closingMode}
              options={(["perNote", "lines"] as const).map((o) => ({ value: o, label: BUNDLE_MODE_LABEL[o] }))}
              disabled={!admin || saveClosing.isPending}
              onChange={(v) => q.data && q.data.closingMode !== v && saveClosing.mutate({ closingMode: v === "lines" ? "lines" : "perNote" })}
              selectTestId="closing-mode-default"
            />
          ) : null}
        </GroupList>
      </GroupSection>

      {/* #213（docs/40 §2）：パートナー手数料（モジュール referral）と上乗せ後の単価の丸め */}
      <GroupSection
        title={t(REFERRAL_MSG.section)}
        help={
          <Help
            testId="help-referral-settings"
            text={helpOf([
              [t(REFERRAL_MSG.feature), t(REFERRAL_MSG.featureHelp)],
              [REFERRAL_MSG.round, t(REFERRAL_MSG.roundHelp)],
            ])}
          />
        }
        data-testid="est-sec-referral"
      >
        <GroupList>
          <GroupSwitchRow label={t(REFERRAL_MSG.feature)} name={t(REFERRAL_MSG.feature)} checked={cf.referralFee} disabled={!admin} onChange={(v) => toggleFlag({ referralFee: v })} testId="sw-referral" rowTestId="row-referral" />
          {/* 名前の下に全幅のセグメント（端数処理と同じ形。スマホ 375・文字「大」でもはみ出さない） */}
          <GroupValueRow
            title={REFERRAL_MSG.round}
            titleId={roundId}
            className="grow-segwide"
            value={
              <Segmented
                options={MARKUP_ROUNDS.map((n) => ({ value: String(n), label: REFERRAL_MSG.roundUnit.replace("{n}", String(n)), testId: `markup-round-${n}` }))}
                value={String(cf.markupRound)}
                onChange={(v) => {
                  const r = Number(v) as MarkupRound;
                  if (!hasName) {
                    toast(SETTINGS_MSG.needCompanyName);
                    return;
                  }
                  setCf((f) => (f ? { ...f, markupRound: r } : f));
                  saveRound.mutate(r);
                }}
                labelledBy={roundId}
                disabled={!admin || saveRound.isPending}
                testId="markup-round"
                className="is-wide"
              />
            }
          />
          {/* {vendorQuote}の節が無い会社は、行の詳細の粗利率の既定をここで（同じ欄） */}
          {!can("vendorQuote") && cf.referralFee ? (
            <GroupNumberRow id="st_margin_ref" label={SETTINGS_MSG.margin} kind="dec" unit="%" value={nums.vendorMarginPct} disabled={!admin} error={cErrors.vendorMarginPct} onChange={(v) => setNum({ vendorMarginPct: v == null ? 0 : Math.round(v * 10) / 10 })} inputTestId="vendor-margin-ref" />
          ) : null}
        </GroupList>
      </GroupSection>

      {/* 拾い出し（#130、docs/47）：内訳のない一式の知らせの基準（社内用） */}
      <GroupSection
        title={SETTINGS_MSG.sectionLumpsum}
        help={<Help testId="help-lumpsum" text={t(`単位が「式」で、内訳の行・${can("takeoff") ? "{takeoff}・" : ""}メモ・仕入元${can("vendorQuote") ? "（{vendorQuote}）" : ""}のどれも無い行が、この基準を超えると見積の画面に知らせます。発行は止めません。0 にした基準は使いません。既定は 10％・100,000円。`)} />}
        note={SETTINGS_MSG.lumpsumNote}
        data-testid="est-sec-lumpsum"
      >
        <GroupList>
          <GroupNumberRow id="st_lump_pct" label={SETTINGS_MSG.lumpsumPct} kind="int" unit="%" value={nums.lumpsumWarnPct} disabled={!admin} error={cErrors.lumpsumWarnPct} onChange={(v) => setNum({ lumpsumWarnPct: v == null ? 0 : v })} inputTestId="lumpsum-pct" />
          <GroupNumberRow id="st_lump_yen" label={SETTINGS_MSG.lumpsumYen} kind="int" unit="円" value={nums.lumpsumWarnYen} disabled={!admin} error={cErrors.lumpsumWarnYen} onChange={(v) => setNum({ lumpsumWarnYen: v == null ? 0 : v })} inputTestId="lumpsum-yen" />
        </GroupList>
      </GroupSection>

      {can("vendorQuote") ? (
        <GroupSection
          title={t(SETTINGS_MSG.sectionVendor)}
          help={<Help testId="help-margin" text={`見積書の明細の「取込」で${L.vendorQuote}の行を入れるときと、行の詳細の粗利率の既定です（売価に対する％）。取込のたびに変えられます。0〜99.9。単価は 仕入れの単価 ÷ (1 − 粗利率) を「${REFERRAL_MSG.round}」の単位で切り上げます（20% なら上乗せ率 25%）。`} />}
          data-testid="est-sec-vendor"
        >
          <GroupList>
            <GroupNumberRow id="st_margin" label={SETTINGS_MSG.margin} kind="dec" unit="%" value={nums.vendorMarginPct} disabled={!admin} error={cErrors.vendorMarginPct} onChange={(v) => setNum({ vendorMarginPct: v == null ? 0 : Math.round(v * 10) / 10 })} inputTestId="vendor-margin" />
          </GroupList>
        </GroupSection>
      ) : null}

      {/* 見積の区分（#129、docs/47）：「〃」・別途の一覧（#308、docs/103 §3）。出した見積は出した時点の設定で固まる。拾い出し（#130）：積算表は使う機能「{takeoff}」の会社だけ（#142 段3） */}
      <GroupSection
        title={SETTINGS_MSG.sectionPdf}
        help={
          <Help
            testId="help-pdf"
            text={helpOf([
              [SETTINGS_MSG.pageBreak, "表紙＋内訳書のとき。表紙のあとは必ず改ページします。オフ（既定）：内訳どうしは続けて印字します（内訳の見出しと表の見出ししか入らないときだけ次のページへ）。オン：内訳ごとに新しいページから印字します。"],
              [t(SETTINGS_MSG.ditto), t("見出しの中で前の行と{lineName}が同じふつうの行を、帳票では「〃」と印字します。データ（{lineName}）は変わりません。見積書・請求書・領収書。発行済みの帳票は変わりません。")],
              [SETTINGS_MSG.scopeNotes, "区分「別途」の行の名称を、見積書の備考の先頭に「別途：…」の1行で印字します。発行済みの見積書は変わりません。"],
              ...(can("takeoff") ? ([[t(SETTINGS_MSG.takeoffSheet), t("{takeoff}（場所・式・数量）のある見積書の PDF の最後に、{takeoffSheet}のページを付けます（オフ＝既定）。メモ・写真は載りません。発行済みの帳票は発行時の設定のままです。")]] as Array<[string, string]>) : []),
            ])}
          />
        }
        data-testid="est-sec-pdf"
      >
        <GroupList>
          <GroupSwitchRow label={SETTINGS_MSG.pageBreak} name={SETTINGS_MSG.pageBreak} checked={cf.pdfPageBreakPerSheet} disabled={!admin} onChange={(v) => toggleFlag({ pdfPageBreakPerSheet: v })} testId="sw-pagebreak" rowTestId="row-pagebreak" />
          <GroupSwitchRow label={t(SETTINGS_MSG.ditto)} name={t(SETTINGS_MSG.ditto)} checked={cf.printDitto} disabled={!admin} onChange={(v) => toggleFlag({ printDitto: v })} testId="sw-ditto" rowTestId="row-ditto" />
          <GroupSwitchRow label={SETTINGS_MSG.scopeNotes} name={SETTINGS_MSG.scopeNotes} checked={cf.printScopeNotes} disabled={!admin} onChange={(v) => toggleFlag({ printScopeNotes: v })} testId="sw-scope" rowTestId="row-scope" />
          {can("takeoff") ? (
            <GroupSwitchRow label={t(SETTINGS_MSG.takeoffSheet)} name={t(SETTINGS_MSG.takeoffSheet)} checked={cf.printTakeoffSheet} disabled={!admin} onChange={(v) => toggleFlag({ printTakeoffSheet: v })} testId="sw-takeoff-sheet" rowTestId="row-takeoff-sheet" />
          ) : null}
        </GroupList>
      </GroupSection>

      {/* メールの文面（#225、docs/91 §3.5）：書類の種類ごとの件名・本文（下の画面）。右に件名の頭 */}
      <GroupSection title={MAIL_MSG.settingsSection} help={<Help testId="help-mail-templates" text={MAIL_MSG.settingsHelp} />} data-testid="est-sec-mail">
        <GroupList>
          {/* #233：{order}はモジュール sales の会社だけ（承諾ページのリンクを本文に差し込める） */}
          {/* #233 2a：辞退の連絡（不採用の{vendor}へ・任意）は{vendorQuote}を使う会社だけ。2b：{order}の訂正版（revise）は{order}のすぐ下 */}
          {/* #266：{deliveryNote}は sales の会社で使う機能「{deliveryNote}」がオンのとき（見積書・請求書のあいだ） */}
          {(["quote", ...(sales && can("deliveryNote") ? (["delivery_note"] as const) : []), "invoice", "receipt", "rfq", ...(sales ? (["order", "revise", "payment_notice"] as const) : []), ...(can("vendorQuote") ? (["decline"] as const) : [])] as const).map((k) => (
            <GroupNavRow key={k} title={t(MAIL_KIND_LABEL[k])} value={mailTpl.data?.[k].subject ?? ""} valueTestId={`mail-template-value-${k}`} to={`/settings/estimate/mail/${k}`} buttonId={`toMail-${k}`} testId={`mail-template-row-${k}`} />
          ))}
        </GroupList>
      </GroupSection>

      {/* 売上の基準（#266、docs/96 決めごと16）：sales の会社で使う機能「{deliveryNote}」がオンのときだけ（使わない会社は請求日のまま） */}
      {sales && can("deliveryNote") && cq.data ? (
        <GroupSection title={DELIVERY_NOTE_MSG.revenueBasis} help={<Help testId="help-revenue-basis" text={t(DELIVERY_NOTE_MSG.revenueBasisHelp)} />} data-testid="est-sec-revenue">
          <GroupList>
            <GroupSelectRow
              id="st_revenue"
              label={DELIVERY_NOTE_MSG.revenueBasis}
              className="is-end"
              value={cq.data.revenueBasis ?? "invoice"}
              options={(["invoice", "delivery", ...(cq.data.revenueBasis === "acceptance" || uses(me.data, "acceptance") ? (["acceptance"] as const) : [])] as const).map((b) => ({ value: b, label: REVENUE_BASIS_LABEL[b] }))}
              disabled={!admin || saveBasis.isPending}
              onChange={(v) => {
                if (!hasName) {
                  toast(SETTINGS_MSG.needCompanyName);
                  return;
                }
                if (v !== cq.data?.revenueBasis) saveBasis.mutate(v as RevenueBasis);
              }}
              selectTestId="st-revenue"
            />
          </GroupList>
        </GroupSection>
      ) : null}

      {/* 定期請求（#268、Figma ②-2 283:513）：下書きの自動作成・作成時期・作成の通知。sales の会社だけ。今の設定の型（押した瞬間に保存）で */}
      {sales && q.data ? (
        <GroupSection title={RECURRING_MSG.settingsSection} help={<Help testId="help-recurring-settings" text={RECURRING_MSG.settingsHelp} />} data-testid="est-sec-recurring">
          <GroupList>
            <GroupSwitchRow
              label={RECURRING_MSG.autoLabel}
              name={RECURRING_MSG.autoLabel}
              checked={recAuto ?? q.data.recurringAuto}
              disabled={!admin || saveRec.isPending}
              onChange={(v) => {
                setRecAuto(v);
                saveRec.mutate({ recurringAuto: v });
              }}
              testId="sw-recurring-auto"
              rowTestId="row-recurring-auto"
            />
            <GroupSelectRow
              id="st_recurring_lead"
              label={RECURRING_MSG.leadLabel}
              className="is-end"
              value={String(saveRec.isPending && saveRec.variables?.recurringLeadDays ? saveRec.variables.recurringLeadDays : q.data.recurringLeadDays)}
              options={[...new Set([3, 5, 7, 10, 14, q.data.recurringLeadDays])].sort((a, b) => a - b).map((n) => ({ value: String(n), label: RECURRING_MSG.leadValue(n) }))}
              disabled={!admin || saveRec.isPending || !(recAuto ?? q.data.recurringAuto)}
              onChange={(v) => q.data && Number(v) !== q.data.recurringLeadDays && saveRec.mutate({ recurringLeadDays: Number(v) })}
              selectTestId="st-recurring-lead"
            />
            <GroupValueRow title={RECURRING_MSG.notifyLabel} value={RECURRING_MSG.notifyValue} valueTestId="recurring-notify" />
          </GroupList>
        </GroupSection>
      ) : null}

      <GroupSection
        id="unissue"
        title={SETTINGS_MSG.sectionUnissue}
        help={<Help testId="help-unissue" text="オン（既定）：出したあとに修正が出たら、取り消して下書きに戻し、修正して同じ番号で出し直せます。取り消した記録は残ります。受注・失注にした見積と、入金の記録がある請求書は取り消せません。オフ：発行済みの帳票は修正せず、修正するときは次の回（Q-0001-2）として再発行します。" />}
        note={SETTINGS_MSG.unissueNote}
        data-testid="est-sec-unissue"
      >
        <GroupList>
          <GroupSwitchRow label={SETTINGS_MSG.unissue} name={SETTINGS_MSG.unissue} checked={cf.allowQuoteUnissue} disabled={!admin} onChange={(v) => toggleFlag({ allowQuoteUnissue: v })} testId="sw-unissue" rowTestId="row-unissue" />
        </GroupList>
      </GroupSection>

      {nums.shortfallDiscountLimit !== undefined ? (
        <GroupSection
          title={SETTINGS_MSG.sectionPayment}
          help={<Help testId="help-discount-limit" text="入金が請求額より少し足りないとき（1円の端数・数百円の相殺など）、請求書1枚あたりこの額までは「値引きとして処理」で残を 0 にできます。超える不足は「一部入金」のまま残ります。既定 1,000円。" />}
          note={SETTINGS_MSG.paymentNote}
          data-testid="est-sec-payment"
        >
          <GroupList>
            <GroupNumberRow id="st_disc" label={SETTINGS_MSG.discount} kind="int" unit="円" value={nums.shortfallDiscountLimit} disabled={!admin} error={cErrors.shortfallDiscountLimit} onChange={(v) => setNum({ shortfallDiscountLimit: v == null ? 0 : v })} inputTestId="discount-limit" />
          </GroupList>
        </GroupSection>
      ) : null}
      {node}
    </div>
  );
}

/**
 * 設定 → 見積・請求 → 分割の既定（#154、docs/63 §3・★1 a、Figma ⑧。前は見積・請求の中の表）。
 *   ・上の帯「‹ 見積・請求｜自動で保存されます」（S-00008 の続き。前は「保存」）、大きな題「分割の既定」。PC の2列では右に出し、左の列の印は「見積・請求」（contract の settingsRowOf）
 *   ・節「回」：回ごとの行（InstallmentRow。⊖ は回が3つ以上のとき最後の回以外・名前は 20 文字まで・最後の回は「残り」）。注「最後の回は残りです。…」
 *   ・「＋ 回追加」（12回まで。最後の回の前に足す）、会社の既定があるときだけ「既定に戻す（製品の既定の名前）」（確かめは挟まない＝今と同じ）
 *   ・製品の既定のままのときは題の下に注「製品の既定です」
 *   ・自動で保存（lib/autoSave.tsx）：名前・％は欄を離れたとき、⊖ はすぐ、「＋ 回追加」は足した回の名前の欄へ移って欄を離れたとき。PUT /company/estimate-settings
 *     （形の決まりは contract の InstallmentDefaults、誤りは節の下に朱の1行で送らない）
 *   ・新しく作る分割の予定の下書きの回（会社の既定が無ければ製品の既定：現場力＝着手金・中間金・完成時、営業力＝前金・中間金・残金）。今ある分割の予定は変わらない
 *   ・担当者は見るだけ（⊖・回を追加・既定に戻す・保存の様子は出さない）。送れていない直しがあるまま「‹ 見積・請求」・左の列で移ろうとしたら確かめる
 */
export function SettingsInstallmentsPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: KEY, queryFn: () => api<EstimateSettings>("GET", "/company/estimate-settings") });
  const a = useAutoSave<InstallmentDefault[]>({
    server: q.data ? (q.data.installments ?? q.data.installmentsProduct) : null,
    enabled: admin,
    send: async (v) => {
      const r = await api<EstimateSettings>("PUT", "/company/estimate-settings", { installments: v } satisfies EstimateSettingsUpdate);
      qc.setQueryData(KEY, r);
      return r.installments ?? r.installmentsProduct;
    },
    check: (v) => {
      const r = InstallmentDefaults.safeParse(v);
      return r.success ? null : { status: SETTINGS_MSG.autoSaveFailed, errors: { installments: r.error.issues[0]?.message ?? SETTINGS_MSG.autoSaveFailed } };
    },
    hide: (v) => ({ url: "/api/v1/company/estimate-settings", method: "PUT", body: { installments: v } }),
  });
  const inst = a.form;
  const err = a.errors.installments ?? a.serverError;
  const back = useBackNav({ to: "/settings/estimate", label: ESTIMATE_PC_MSG.settingsTitle }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(ESTIMATE_PC_MSG.installmentsTitle);
  /** 既定に戻す：すぐ効く（自動で保存とは別） */
  const save = useMutation({
    mutationFn: (v: EstimateSettingsUpdate) => api<EstimateSettings>("PUT", "/company/estimate-settings", v),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      a.replace(v.installments ?? v.installmentsProduct);
      toast(ESTIMATE_PC_MSG.installmentsResetDone);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || !inst || !me.data) return <p className="hint">読み込んでいます…</p>;
  const put = (i: number, p: Partial<InstallmentDefault>) => a.set((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const addRound = () => {
    // 残り（最後の回）の前に足して、その回の名前の欄へ（すぐは送らない＝名前が空で断られる。欄を離れたときに送る）
    const at = inst.length - 1;
    a.set((xs) => [...xs.slice(0, -1), { label: "", percent: 10 }, xs[xs.length - 1]!], { wait: true });
    window.setTimeout(() => document.getElementById(`idf_n${at}`)?.focus(), 0);
  };
  const removeRound = (i: number) => a.set((xs) => xs.filter((_, j) => j !== i));
  const isProduct = q.data.installments === null;

  return (
    <div className="ppage estpage" data-testid="installment-defaults" {...a.formProps}>
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
        {admin ? <AutoSaveStatus state={a.state} testId="installments-autosave" /> : null}
      </div>
      {back.dialog}
      {guard.dialog}
      <h1 className="ptitle">{ESTIMATE_PC_MSG.installmentsTitle}</h1>
      <AutoSaveNote state={a.state} testId="installments-autosave-note" />
      {isProduct ? (
        <p className="est-note" data-testid="installment-defaults-product">
          {ESTIMATE_PC_MSG.installmentsIsProduct}
        </p>
      ) : null}

      <GroupSection title={ESTIMATE_PC_MSG.installmentsSection} note={ESTIMATE_PC_MSG.installmentsNote} data-testid="installments-sec">
        <GroupList>
          {inst.map((x, i) => {
            const last = i === inst.length - 1;
            const n = ESTIMATE_PC_MSG.installmentsRound.replace("{n}", String(i + 1));
            return (
              <InstallmentRow
                key={i}
                n={n}
                label={x.label}
                percent={x.percent}
                rest={last}
                onLabel={(label) => put(i, { label })}
                onPercent={(percent) => put(i, { percent })}
                onRemove={admin && !last && inst.length > 2 ? () => removeRound(i) : undefined}
                gutter={admin}
                disabled={!admin}
                nameId={`idf_n${i}`}
                percentId={`idf_p${i}`}
                nameLabel={`${n}の${ESTIMATE_PC_MSG.installmentsName}`}
                percentLabel={`${n}の${ESTIMATE_PC_MSG.installmentsPercent}`}
                restLabel={ESTIMATE_PC_MSG.installmentsRest}
                removeLabel={ESTIMATE_PC_MSG.installmentsRemove.replace("{n}", n)}
                removeTestId="installment-default-remove"
                testId="installment-default-row"
              />
            );
          })}
        </GroupList>
        {err ? (
          <p className="gsecerr" role="alert" data-testid="installments-error">
            {`⚠ ${err}`}
          </p>
        ) : null}
      </GroupSection>

      {admin ? (
        <GroupSection>
          <GroupList>
            <GroupRow
              className="co-addrow"
              icon={
                <span className="co-plus" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </span>
              }
              title={ESTIMATE_PC_MSG.installmentsAdd}
              onClick={addRound}
              disabled={inst.length >= 12}
              buttonTestId="installment-default-add"
            />
          </GroupList>
        </GroupSection>
      ) : null}
      {admin && !isProduct ? (
        <GroupSection>
          <GroupList plain>
            <GroupDangerRow tone="ai" title={`${ESTIMATE_PC_MSG.installmentsReset}（${namesOf(q.data.installmentsProduct)}）`} onClick={() => save.mutate({ installments: null })} disabled={save.isPending} buttonTestId="installment-default-reset" />
          </GroupList>
        </GroupSection>
      ) : null}
      {node}
    </div>
  );
}
